/**
 * Layout de login: panel visual (md+) + columna de formulario.
 * Fondo raíz DARK_BG; tema claro/oscuro según useArgonController().darkMode.
 */
import { useEffect } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useArgonController, setLayout } from "context";
import { useLanguage } from "context/LanguageContext";
import LanguageSwitcher from "components/LanguageSwitcher";

import { ACCENT, DARK_BG, LOGO_SRC } from "layouts/authentication/constants/loginTheme";

function LoginLayout({ children }) {
  const [controller, dispatch] = useArgonController();
  const { darkMode } = controller;
  const { t } = useLanguage();

  useEffect(() => {
    setLayout(dispatch, "page");
  }, [dispatch]);

  const mainBg = darkMode ? "#0c121c" : "#ffffff";
  const mainBorder = darkMode ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)";

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: { xs: "100dvh", md: "100vh" },
        width: "100%",
        overflow: "hidden",
        bgcolor: DARK_BG,
      }}
    >
      {/* Panel izquierdo — VisualPane */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: "1 1 0%",
          minWidth: 0,
          minHeight: "100vh",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          px: { md: 7, lg: 9 },
          py: 6,
          background: "linear-gradient(170deg, #0d1626 0%, #0a1220 55%, #081020 100%)",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "-30%",
            left: "-10%",
            width: "70%",
            height: "60%",
            background:
              "radial-gradient(ellipse closest-side, rgba(30,134,246,0.10) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            width: "100%",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.75}>
            <Typography
              component="span"
              sx={{
                fontSize: "1.05rem",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "rgba(255,255,255,0.95)",
              }}
            >
              PayFlex
            </Typography>
            <Box
              sx={{
                width: "1px",
                height: "16px",
                background: "rgba(255,255,255,0.18)",
              }}
            />
            <Typography
              component="span"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 500,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.45)",
              }}
            >
              {t('login.brandLabel')}
            </Typography>
          </Stack>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", flexShrink: 0 }}>
            <Box sx={{ maxWidth: "460px", pb: 4, zIndex: 1 }}>
              <Box
                sx={{
                  width: "36px",
                  height: "3px",
                  background: ACCENT,
                  borderRadius: 0.5,
                  mb: 3.5,
                }}
              />
              <Typography
                sx={{
                  fontSize: { md: "1.9rem", lg: "2.2rem" },
                  fontWeight: 600,
                  letterSpacing: "-0.025em",
                  lineHeight: 1.25,
                  color: "rgba(255,255,255,0.95)",
                  mb: 2,
                }}
              >
                {t("login.leftTitle")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  lineHeight: 1.7,
                  color: "rgba(255,255,255,0.5)",
                  maxWidth: "400px",
                  whiteSpace: "pre-line",
                }}
              >
                {`\n`}{t("login.leftSubtitle")}
              </Typography>
            </Box>
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.3)",
                fontSize: "0.7rem",
                letterSpacing: "0.02em",
              }}
            >
              © {new Date().getFullYear()} PayFlex
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.3)",
                fontSize: "0.7rem",
                letterSpacing: "0.06em",
              }}
            >
              {t("login.internalUse")}
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* Columna derecha — formulario */}
      <Box
        component="main"
        sx={{
          flex: { xs: "1 1 auto", md: "0 0 clamp(420px, 36vw, 500px)" },
          width: { xs: "100%", md: "auto" },
          minHeight: { xs: "100dvh", md: "100vh" },
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          bgcolor: mainBg,
          borderLeft: { xs: "none", md: `1px solid ${mainBorder}` },
          position: "relative",
          zIndex: 1,
          px: { xs: 3, sm: 5, md: 7 },
          pt: { xs: "max(12px, env(safe-area-inset-top, 0px))", md: 6 },
          pb: { xs: "max(12px, env(safe-area-inset-bottom, 0px))", md: 6 },
        }}
      >
        {/* Selector de idioma — esquina superior derecha del panel */}
        <Box
          sx={{
            position: "absolute",
            top: { xs: 14, md: 20 },
            right: { xs: 16, sm: 24, md: 28 },
            zIndex: 10,
          }}
        >
          <LanguageSwitcher darkMode={darkMode} />
        </Box>

        {/* Zona central: flex 1 + centrado vertical */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 0,
            width: "100%",
            py: { xs: 2, md: 0 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "360px",
              mx: "auto",
            }}
          >
            <Box
              component="img"
              src={LOGO_SRC}
              alt="PayFlex"
              sx={{
                height: { xs: "42px", sm: "40px", md: "36px" },
                width: "auto",
                maxWidth: { xs: "220px", sm: "200px", md: "180px" },
                objectFit: "contain",
                objectPosition: "left center",
                display: "block",
                mb: { xs: 2, sm: 2.25, md: 4 },
                mx: 0,
                filter: darkMode ? "brightness(0) invert(1)" : "none",
              }}
            />
            {children}
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: { xs: "block", md: "none" },
            flexShrink: 0,
            textAlign: "center",
            color: "text.disabled",
            pt: 2,
            pb: 0.5,
          }}
        >
          PayFlex Admin
        </Typography>
      </Box>
    </Box>
  );
}

LoginLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LoginLayout;
